import React, { useState, useEffect } from "react";
import {
  Box,
  Stack,
  InputAdornment,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import { SectionLabel, PROVIDER_LABEL } from "./_wizardUi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGitlabSquare,
  faBitbucket,
  faGithub,
} from "@fortawesome/free-brands-svg-icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import GitIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import { useDispatch } from "react-redux";
import { useNotification } from "../../../../context/NotificationContext";
import { AppDispatch } from "../../../../store";
import {
  fetchReleases,
  fetchReleaseAssets,
  registryProcessResource,
} from "../../../../features/slices/Registry";
import {
  fetchSystem,
  postSystem,
} from "../../../../features/slices/SystemRegistry";

import { useLoader } from "../../../../context/LoaderContext";

interface ResourceItem {
  id: number;
  gitUrl: string;
  authType: string;
  username?: string;
  token?: string;
  releaseTag: string;
  assetName: string;
}

interface GitResourceStepProps {
  onFinish: (resources: ResourceItem[]) => void;
  goBack: () => void;
  suiteId: number | string;
  silCases: boolean;
}

const GitResourceStep = ({
  onFinish,
  goBack,
  suiteId,
  silCases,
}: GitResourceStepProps) => {
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newGitUrl, setNewGitUrl] = useState("");
  const [newAuthType, setNewAuthType] = useState("token");
  const [newUsername, setNewUsername] = useState("");
  const [newToken, setNewToken] = useState("");
  const [newSshFile, setNewSshFile] = useState<File | null>(null);
  const [newSshPassphrase, setNewSshPassphrase] = useState("");
  const [newHasPassphrase, setNewHasPassphrase] = useState(false);
  const [newIsValidUrl, setNewIsValidUrl] = useState(false);
  const [newIconType, setNewIconType] = useState<string | null>(null);
  const [newIsFormValid, setNewIsFormValid] = useState(false);

  const [releases, setReleases] = useState<any[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<string>("");
  const [assets, setAssets] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string>("");

  const [loadingReleases, setLoadingReleases] = useState(false);
  const [loadingAssets, setLoadingAssets] = useState(false);

  const [saveCredentials, setSaveCredentials] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const { notify } = useNotification();
  const { showLoader, hideLoader } = useLoader();

  const validateNewUrl = (url: string) => {
    const githubPattern = /^(https:\/\/|git@)github\.com[:\/].*\.git$/;
    const gitlabPattern =
      /^(https:\/\/(?:[^@]+@)?gitlab\.com\/[\w\-]+\/[\w\-]+\.git|git@gitlab\.com:[\w\-]+\/[\w\-]+\.git)$/;
    const bitbucketPattern =
      /^(https:\/\/(?:[^@]+@)?bitbucket\.org\/[\w\-]+\/[\w\-]+\.git|git@bitbucket\.org:[\w\-]+\/[\w\-]+\.git)$/;
    const gitPattern = /^(https:\/\/|git@).*\.git$/;
    if (url.startsWith("git@")) {
      setNewAuthType("ssh_key");
      setNewIsValidUrl(true);
      setNewIconType("git");
      notify("SSH repository detected.", "success");
    } else if (githubPattern.test(url)) {
      setNewAuthType("token");
      setNewIsValidUrl(true);
      setNewIconType("github");
      notify("GitHub repository detected.", "success");
    } else if (gitlabPattern.test(url)) {
      setNewAuthType("token");
      setNewIsValidUrl(true);
      setNewIconType("gitlab");
      notify("GitLab repository detected.", "success");
    } else if (bitbucketPattern.test(url)) {
      setNewAuthType("token");
      setNewIsValidUrl(true);
      setNewIconType("bitbucket");
      notify("Bitbucket repository detected.", "success");
    } else if (gitPattern.test(url)) {
      setNewAuthType("token");
      setNewIsValidUrl(true);
      setNewIconType("git");
      notify("Git repository detected.", "success");
    } else {
      setNewIsValidUrl(false);
      setNewIconType(null);
      notify("Invalid Git URL. Please enter a valid repository URL.", "error");
    }
  };

  useEffect(() => {
    const isFormValid = () => {
      if (newAuthType === "token") {
        return (
          newIsValidUrl && newUsername.trim() !== "" && newToken.trim() !== ""
        );
      } else if (newAuthType === "ssh_key") {
        return newIsValidUrl && newSshFile !== null;
      }
      return false;
    };
    setNewIsFormValid(isFormValid());
  }, [
    newAuthType,
    newGitUrl,
    newUsername,
    newToken,
    newSshFile,
    newIsValidUrl,
  ]);

  const handleNewFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setNewSshFile(e.target.files[0]);
    }
  };

  const loadCredentials = async (type: string) => {
    const params = {
      reg_type: type,
      repo: newGitUrl,
      mode: "first",
      entity_type: "resources",
    };
    const query = new URLSearchParams(params).toString();
    try {
      const response = await dispatch(fetchSystem(query)).unwrap();
      if (response) {
        setSaveCredentials(true);
        const regValue = response.reg_value || {};
        setNewUsername(regValue.username || "");
        setNewToken(regValue.token || "");
        notify("Credentials loaded successfully", "success");
      }
    } catch (error) {
      setSaveCredentials(false);
      notify("No credentials found for this repository.", "warning");
      console.error("Failed to load credentials:", error);
    }
  };

  useEffect(() => {
    if (newGitUrl && newAuthType) {
      loadCredentials(newAuthType);
    }
  }, [newGitUrl, newAuthType]);

  const handleSaveCredentials = async () => {
    if (saveCredentials) {
      const formData = new FormData();
      formData.append("repo", newGitUrl);
      const title = newGitUrl.split("/").pop()?.replace(".git", "") || "";
      formData.append("title", title);
      formData.append("reg_type", newAuthType);
      formData.append("entity_type", "resources");
      const regValue = JSON.stringify({
        username: newUsername,
        token: newToken,
      });
      formData.append("reg_value", regValue);
      if (newAuthType === "ssh_key" && newSshFile) {
        formData.append("file", newSshFile);
      }
      try {
        await dispatch(postSystem(formData)).unwrap();
        notify("Credentials saved successfully", "success");
      } catch (error) {
        notify("Failed to save credentials", "error");
      }
    }
  };

  const fetchReleasesHandler = async () => {
    if (!newIsValidUrl || !newIsFormValid) {
      notify(
        "Please fill out the repository URL and required credentials.",
        "error"
      );
      return;
    }
    setLoadingReleases(true);
    const requestData = {
      repo_url: newGitUrl,
      username: newAuthType === "token" ? newUsername : "",
      password: newAuthType === "token" ? newToken : "",
      token: newAuthType === "token" ? newToken : "",
    };
    try {
      const response = await dispatch(fetchReleases(requestData)).unwrap();
      setReleases(response || []);
      setLoadingReleases(false);
      if (response.length > 0) {
        notify("Releases fetched successfully!", "success");
        if (saveCredentials) {
          await handleSaveCredentials();
        }
      } else {
        notify("No releases found in the repository.", "warning");
      }
    } catch (error) {
      setLoadingReleases(false);
      notify("Failed to fetch releases. Please try again.", "error");
    }
  };

  const fetchAssetsHandler = async () => {
    if (!selectedRelease) {
      notify("Please select a release first.", "error");
      return;
    }
    setLoadingAssets(true);
    const requestData = {
      repo_url: newGitUrl,
      release_tag: selectedRelease,
      username: newAuthType === "token" ? newUsername : "",
      password: newAuthType === "token" ? newToken : "",
      token: newAuthType === "token" ? newToken : "",
    };
    try {
      const response = await dispatch(fetchReleaseAssets(requestData)).unwrap();
      setAssets(response || []);
      setLoadingAssets(false);
      if (response.length > 0) {
        notify("Assets fetched successfully!", "success");
      } else {
        notify("No assets found for the selected release.", "warning");
      }
    } catch (error) {
      setLoadingAssets(false);
      notify("Failed to fetch assets. Please try again.", "error");
    }
  };

  const getNewIcon = () => {
    switch (newIconType) {
      case "github":
        return <FontAwesomeIcon icon={faGithub} size="2x" />;
      case "gitlab":
        return (
          <FontAwesomeIcon icon={faGitlabSquare} color="orange" size="2x" />
        );
      case "bitbucket":
        return <FontAwesomeIcon icon={faBitbucket} color="blue" size="2x" />;
      case "git":
        return <GitIcon color="action" />;
      default:
        return null;
    }
  };

  const handleAddResource = () => {
    if (!selectedRelease) {
      notify("Please select a release.", "error");
      return;
    }
    if (!selectedAsset) {
      notify("Please select an asset (EXE) from the release.", "error");
      return;
    }
    const newResource: ResourceItem = {
      id: new Date().getTime(),
      gitUrl: newGitUrl,
      authType: newAuthType,
      username: newAuthType === "token" ? newUsername : undefined,
      token: newAuthType === "token" ? newToken : undefined,
      releaseTag: selectedRelease,
      assetName: selectedAsset,
    };
    setResources((prev) => [...prev, newResource]);
    notify("Resource added successfully!", "success");
    setNewGitUrl("");
    setNewUsername("");
    setNewToken("");
    setNewSshFile(null);
    setNewSshPassphrase("");
    setNewHasPassphrase(false);
    setSelectedRelease("");
    setSelectedAsset("");
    setReleases([]);
    setAssets([]);
    setNewIsValidUrl(false);
    setNewIconType(null);
    setShowAddForm(false);
    setSaveCredentials(false);
  };

  const handleSubmitResources = async () => {
    if (silCases && resources.length === 0) {
      notify(
        "You have selected SIL tests; please add at least one corresponding resource.",
        "error"
      );
      return;
    }
    showLoader();
    let allSuccessful = true;
    for (const res of resources) {
      const requestData = {
        repo_url: res.gitUrl,
        username: res.authType === "token" ? res.username || "" : "",
        token: res.authType === "token" ? res.token || "" : "",
        reg_type: "git",
        tag: res.releaseTag,
        asset_name: res.assetName,
        suite_id: `${suiteId}`,
      };
      try {
        const response = await dispatch(
          registryProcessResource(requestData)
        ).unwrap();
        if (typeof response !== "string") {
          allSuccessful = false;
          notify(`Failed to process resource: ${res.gitUrl}`, "error");
        }
      } catch (error) {
        allSuccessful = false;
        notify(`Error processing resource: ${res.gitUrl}`, "error");
      }
    }

    hideLoader();

    if (allSuccessful) {
      notify("All resources processed successfully!", "success");
      onFinish(resources);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography sx={{ fontWeight: 800, fontSize: 18, lineHeight: 1.2 }}>
          Resources
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          Optional. Attach emulator/device binaries the tests need (e.g. a SIL
          emulator). Skip this step if your cases need no extra resources.
        </Typography>
      </Box>
      {!showAddForm && (
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setShowAddForm(true)}
          sx={{ alignSelf: "flex-start" }}
        >
          Add resource
        </Button>
      )}
      {showAddForm && (
        <Box
          sx={{
            mt: 2,
            p: { xs: 2, sm: 2.5 },
            border: "1px solid var(--app-border, #e4e7ec)",
            borderRadius: "10px",
            backgroundColor: "var(--app-surface-muted, #f8fafc)",
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 16, mb: 2 }}>
            New resource
          </Typography>

          <Stack spacing={3}>
            {/* Repository */}
            <Box>
              <SectionLabel>Repository</SectionLabel>
              <TextField
                autoComplete="off"
                label="Git repository URL"
                placeholder="https://github.com/org/emulator.git"
                value={newGitUrl}
                onChange={(e) => {
                  setNewGitUrl(e.target.value);
                  validateNewUrl(e.target.value);
                }}
                fullWidth
                error={Boolean(newGitUrl) && !newIsValidUrl}
                helperText={
                  !newGitUrl
                    ? "Repository that holds the emulator/resource releases."
                    : newIsValidUrl
                    ? `Detected: ${
                        PROVIDER_LABEL[newIconType ?? "git"] ?? "Git"
                      } repository`
                    : "That doesn't look like a valid .git URL."
                }
                InputProps={{
                  endAdornment: newGitUrl ? (
                    <InputAdornment position="end">
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {getNewIcon()}
                        {newIsValidUrl ? (
                          <CheckCircleIcon color="success" fontSize="small" />
                        ) : (
                          <CancelIcon color="error" fontSize="small" />
                        )}
                      </Stack>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            {/* Authentication */}
            <Box
              sx={{
                opacity: newIsValidUrl ? 1 : 0.5,
                pointerEvents: newIsValidUrl ? "auto" : "none",
                transition: "opacity 150ms",
              }}
            >
              <SectionLabel>Authentication</SectionLabel>
              <Stack spacing={2}>
                <FormControl fullWidth disabled={!newIsValidUrl}>
                  <InputLabel>Authentication type</InputLabel>
                  <Select
                    value={newAuthType}
                    label="Authentication type"
                    onChange={(e) => setNewAuthType(e.target.value)}
                  >
                    <MenuItem value="token">Token</MenuItem>
                    {/* <MenuItem value="ssh_key">SSH Key</MenuItem> */}
                  </Select>
                </FormControl>

                {newAuthType === "token" && (
                  <>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                      <TextField
                        autoComplete="off"
                        label="Username"
                        value={newUsername}
                        onChange={(e) => setNewUsername(e.target.value)}
                        fullWidth
                      />
                      <TextField
                        autoComplete="off"
                        label="Access token"
                        type="password"
                        value={newToken}
                        onChange={(e) => setNewToken(e.target.value)}
                        fullWidth
                      />
                    </Stack>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={saveCredentials}
                          onChange={(e) => setSaveCredentials(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Remember these credentials for this repository
                        </Typography>
                      }
                    />
                  </>
                )}

                {newAuthType === "ssh_key" && (
                  <Stack spacing={1.5} alignItems="flex-start">
                    <Button variant="outlined" component="label">
                      Upload SSH key file
                      <input type="file" hidden onChange={handleNewFileUpload} />
                    </Button>
                    {newSshFile && (
                      <Typography variant="body2" color="text.secondary">
                        {newSshFile.name} (
                        {(newSshFile.size / 1024).toFixed(2)} KB)
                      </Typography>
                    )}
                    {newSshFile && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={newHasPassphrase}
                            onChange={(e) =>
                              setNewHasPassphrase(e.target.checked)
                            }
                          />
                        }
                        label="This SSH key requires a passphrase"
                      />
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>

            {/* Release & asset */}
            <Box
              sx={{
                opacity: newIsFormValid ? 1 : 0.5,
                pointerEvents: newIsFormValid ? "auto" : "none",
                transition: "opacity 150ms",
              }}
            >
              <SectionLabel>Release &amp; asset</SectionLabel>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", sm: "center" }}
                >
                  <Button
                    variant="outlined"
                    onClick={fetchReleasesHandler}
                    disabled={!newIsFormValid || loadingReleases}
                    startIcon={
                      loadingReleases ? <CircularProgress size={18} /> : null
                    }
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Fetch tags
                  </Button>
                  {releases.length > 0 && (
                    <FormControl sx={{ flex: 1, minWidth: 220 }}>
                      <InputLabel>Release (tag)</InputLabel>
                      <Select
                        value={selectedRelease}
                        label="Release (tag)"
                        onChange={(e) => setSelectedRelease(e.target.value)}
                      >
                        {releases.map((rel) => (
                          <MenuItem key={rel.tag_name} value={rel.tag_name}>
                            {rel.tag_name} - {rel.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Stack>

                {selectedRelease && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "center" }}
                  >
                    <Button
                      variant="outlined"
                      onClick={fetchAssetsHandler}
                      disabled={loadingAssets}
                      startIcon={
                        loadingAssets ? <CircularProgress size={18} /> : null
                      }
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Discover resources
                    </Button>
                    {assets.length > 0 && (
                      <FormControl sx={{ flex: 1, minWidth: 220 }}>
                        <InputLabel>Asset (binary)</InputLabel>
                        <Select
                          value={selectedAsset}
                          label="Asset (binary)"
                          onChange={(e) => setSelectedAsset(e.target.value)}
                        >
                          {assets.map((asset) => (
                            <MenuItem key={asset.name} value={asset.name}>
                              {asset.name} ({(asset.size / 1024).toFixed(2)} KB)
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Stack>
                )}
              </Stack>
            </Box>

            <Divider />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddResource}
                disabled={!selectedRelease || !selectedAsset}
              >
                Add resource
              </Button>
            </Box>
          </Stack>
        </Box>
      )}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6">Added Resources</Typography>
        {resources.length === 0 ? (
          <Typography>No resources added yet.</Typography>
        ) : (
          resources.map((resource) => (
            <Accordion
              key={resource.id}
              sx={{ borderRadius: "10px", overflow: "hidden", boxShadow: 2 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: "#f5f5f5", padding: "12px" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ flexGrow: 1 }}
                  >
                    {resource.assetName} ({resource.releaseTag})
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ padding: "16px", backgroundColor: "#fafafa" }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Repository:</strong> {resource.gitUrl}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  <strong>Release Tag:</strong> {resource.releaseTag}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Asset Name:</strong> {resource.assetName}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  Authentication:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Type:</strong> {resource.authType}
                </Typography>
                {resource.authType === "token" && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Username:</strong> {resource.username}
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
        <Button variant="outlined" color="primary" onClick={goBack}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitResources}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default GitResourceStep;
